import React from "react";
import { BubbleData } from "./types";
import { useBubble } from "./BubbleContext";
import "./chat-bubble.css";

export interface ChatBubbleProps {
  bubble: BubbleData;
  isClosing: boolean;
  onClose: () => void;
  onAnimationEnd?: () => void;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({
  bubble,
  isClosing,
  onClose,
  onAnimationEnd,
}) => {
  const bubbleRef = React.useRef<HTMLDivElement>(null);
  const { isExpanded, expandBubble, collapseBubble } = useBubble();

  // Keep the bubble's topmost visual point (including the jut-out action
  // pill) inside its nearest clipping ancestor: measure overflow past the
  // configured margin and shift down via CSS var.
  React.useLayoutEffect(() => {
    const el = bubbleRef.current;
    if (!el) return;

    const clipTop = (): number => {
      for (
        let ancestor = el.parentElement;
        ancestor;
        ancestor = ancestor.parentElement
      ) {
        const { overflowY } = getComputedStyle(ancestor);
        if (overflowY !== "visible") {
          return ancestor.getBoundingClientRect().top;
        }
      }
      return 0;
    };

    const clamp = () => {
      el.style.setProperty("--bubble-viewport-shift", "0px");
      const margin = parseFloat(
        getComputedStyle(el).getPropertyValue("--bubble-viewport-margin"),
      );
      const actions = el.querySelector(".bubble-actions");
      const visualTop = Math.min(
        el.getBoundingClientRect().top,
        actions ? actions.getBoundingClientRect().top : Infinity,
      );
      const overflow = clipTop() + margin - visualTop;
      if (overflow > 0) {
        el.style.setProperty("--bubble-viewport-shift", `${overflow}px`);
      }
    };

    clamp();
    const observer = new ResizeObserver(clamp);
    observer.observe(el);
    window.addEventListener("resize", clamp);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", clamp);
    };
  }, [bubble.id, isExpanded, isClosing]);

  // Auto-focus the first focusable element in the content on open.
  // Content can mount asynchronously, so watch until one appears.
  React.useLayoutEffect(() => {
    if (isClosing) return;
    const el = bubbleRef.current;
    if (!el) return;

    const focusable = () =>
      el.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), [contenteditable="true"]',
      );

    const target = focusable();
    if (target) {
      target.focus();
      return;
    }

    const observer = new MutationObserver(() => {
      const found = focusable();
      if (found) {
        observer.disconnect();
        found.focus();
      }
    });
    observer.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["contenteditable", "disabled"],
    });
    return () => observer.disconnect();
  }, [bubble.id, isClosing, isExpanded]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    collapseBubble();
  };

  if (isExpanded && !isClosing) {
    return (
      <div className="bubble-backdrop" onClick={handleBackdropClick}>
        <div ref={bubbleRef} className="chat-bubble expanded" onClick={e => e.stopPropagation()}>
          <div className="bubble-content">
            <div className="bubble-actions expanded">
              <button className="bubble-action-btn" onClick={collapseBubble} aria-label="Collapse">
                ⤡
              </button>
              <button className="bubble-action-btn" onClick={collapseBubble} aria-label="Close">
                ×
              </button>
            </div>
            <div className="bubble-content-inner">
              {bubble.content}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={bubbleRef}
      className={`chat-bubble ${bubble.side} ${isClosing ? "closing" : "opening"}`}
      style={{
        top: `${bubble.verticalPosition}%`,
      }}
      onAnimationEnd={onAnimationEnd}
    >
      <div className="bubble-content">
        <div className={`bubble-actions ${bubble.side}`}>
          <button className="bubble-action-btn" onClick={expandBubble} aria-label="Expand">
            ⤢
          </button>
          <button className="bubble-action-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="bubble-content-inner">
          {bubble.content}
        </div>
      </div>
      <div className="bubble-tail" />
    </div>
  );
};
